/**
 * Finnhub stock search + quote API.
 * Free tier: 60 API calls/minute. https://finnhub.io
 *
 * Uses symbol search to find tickers, then batch-fetches quotes
 * for price, change, market cap, etc.
 */
import {useQuery} from '@tanstack/react-query'

import {FINNHUB_API_KEY} from '#/env/common'
import {STALE} from '#/state/queries'

const STOCK_SEARCH_ROOT = 'stock-search'
const MAX_RESULTS = 10

const FINNHUB_BASE = 'https://finnhub.io/api/v1'

export type StockSearchItem = {
  symbol: string
  description: string
  type: string
  exchange: string
  price?: number
  change?: number
  changePercent?: number
  high?: number
  low?: number
  open?: number
  previousClose?: number
}

type FinnhubSearchResult = {
  count: number
  result: Array<{
    description: string
    displaySymbol: string
    symbol: string
    type: string
    primary?: string[]
  }>
}

type FinnhubQuote = {
  c: number  // current price
  d: number  // change
  dp: number // percent change
  h: number  // high
  l: number  // low
  o: number  // open
  pc: number // previous close
  t: number  // timestamp
}

async function fetchStockSearch(
  query: string,
): Promise<StockSearchItem[]> {
  if (!FINNHUB_API_KEY) {
    throw new Error('EXPO_PUBLIC_FINNHUB_API_KEY is not set')
  }

  const searchRes = await fetch(
    `${FINNHUB_BASE}/search?` +
      new URLSearchParams({
        q: query,
        token: FINNHUB_API_KEY,
      }),
  )
  if (!searchRes.ok) {
    throw new Error(`Stock search failed (${searchRes.status})`)
  }

  const searchData: FinnhubSearchResult = await searchRes.json()

  // Filter to common stock types and US exchanges, limit results
  const filtered = searchData.result
    .filter(
      r =>
        r.type === 'Common Stock' ||
        r.type === 'ETP' ||
        r.type === 'REIT' ||
        r.type === 'ADR',
    )
    .slice(0, MAX_RESULTS)

  if (filtered.length === 0) return []

  // Fetch quotes for each symbol in parallel
  const quotePromises = filtered.map(async item => {
    try {
      const quoteRes = await fetch(
        `${FINNHUB_BASE}/quote?` +
          new URLSearchParams({
            symbol: item.symbol,
            token: FINNHUB_API_KEY,
          }),
      )
      if (!quoteRes.ok) return null
      const quote: FinnhubQuote = await quoteRes.json()
      if (quote.c === 0 && quote.d === 0) return null
      return {symbol: item.symbol, quote}
    } catch {
      return null
    }
  })

  const quotes = await Promise.all(quotePromises)
  const quoteMap = new Map<string, FinnhubQuote>()
  for (const q of quotes) {
    if (q) quoteMap.set(q.symbol, q.quote)
  }

  return filtered
    .map(item => {
      const quote = quoteMap.get(item.symbol)
      return {
        symbol: item.symbol,
        description: item.description,
        type: item.type,
        exchange: item.displaySymbol,
        price: quote?.c,
        change: quote?.d,
        changePercent: quote?.dp,
        high: quote?.h,
        low: quote?.l,
        open: quote?.o,
        previousClose: quote?.pc,
      }
    })
    .filter(item => item.price != null && item.price > 0)
}

export function useStockSearchQuery({
  query,
  enabled,
}: {
  query: string
  enabled: boolean
}) {
  return useQuery({
    queryKey: [STOCK_SEARCH_ROOT, query],
    queryFn: () => fetchStockSearch(query),
    staleTime: STALE.MINUTES.ONE,
    enabled: enabled && !!query.trim() && !!FINNHUB_API_KEY,
  })
}

export function isStockSearchEnabled() {
  return !!FINNHUB_API_KEY
}
